/**
 * bimWasteTankParts — the biological waste-water treatment plant's geometry,
 * ported from the house-ifc Python module (`devices/waste-tank/waste_tank.py`).
 *
 * A compact BIOLOGICAL treatment plant ("biološka čistilna naprava"): a
 * horizontal vessel in three chambers — primary settling, aeration, final
 * clarification — on two skids, with green lids, an aeration blower cabinet and
 * its air line, a vent stack and the inlet/outlet stubs.
 *
 * ── Why its own family and not a fourth cistern shape ──────────────────────
 *
 * A cistern STORES clean water for the house; a plant PROCESSES dirty water.
 * The hub files them under different classes (`water_tank` / `waste_tank`) and
 * the demo shows both, so each device is offered its own model. The geometry
 * toolkit (box / cylinder / segment profile) is IMPORTED from
 * `bimWaterTankParts` — one source for the primitives, two products.
 *
 * ── The three levels ───────────────────────────────────────────────────────
 *
 * The hub reports ONE reading (`level_percentage`). The chambers are drawn
 * STEPPED off it — aeration ~6 % higher, clarifier ~4 % lower — because three
 * identical tubes read as a bottle, while stepped surfaces read as a plant
 * processing water. The offsets are the module's `CHAMBER_RISE`.
 *
 * Frame (metres): origin = the footprint's centre on the floor; +X along the
 * vessel, +Y its depth, +Z up — the module's own frame, so the port is a plain
 * mm → m conversion and the device pin stands the plant on the ground.
 */

import type { Vec3 } from './bimShadeSpec';
import {
  tankBox,
  tankCylinderX,
  tankCylinderZ,
  tankSegmentProfile,
  waterTankLevelPct,
  WATER_TANK_SEGMENTS,
  type WaterTankPart,
  type WaterTankPartShape,
} from './bimWaterTankParts';

export type { Vec3 };

const MM = 0.001;

/** `devices/waste-tank/waste_tank.py` — the plant's own constants (mm → m). */
export const WASTE_PLANT = {
  /** `PL_L` / `PL_RY` / `PL_RZ` / `PL_ZC` — the vessel. */
  length: 2400 * MM,
  ry: 650 * MM,
  rz: 600 * MM,
  zc: 750 * MM,
  /** `SKID_*` — the two skids it stands on. */
  skidH: 150 * MM,
  skidW: 160 * MM,
  skidD: 1300 * MM,
  skidX: 800 * MM,
  /** `BAFFLE_X` / `BAFFLE_T` — the two partition walls. */
  baffleX: [-400 * MM, 400 * MM] as const,
  baffleT: 30 * MM,
  /** `CHAMBER_RISE` — aeration higher, clarifier lower. */
  chamberRise: [0, 0.06, -0.04] as const,
  /** `LID_*` — the green covers. */
  lidX: [-800 * MM, 0, 800 * MM] as const,
  lidD: 600 * MM,
  lidH: 34 * MM,
  lidNeckD: 520 * MM,
  lidNeckH: 55 * MM,
  /** `BLOWER_*` + the air line. */
  blowerX: 1450 * MM,
  blowerW: 420 * MM,
  blowerD: 340 * MM,
  blowerH: 520 * MM,
  airPipeD: 90 * MM,
  airPipeZ: 1150 * MM,
  /** `VENT_*` — the vent stack. */
  ventD: 80 * MM,
  ventTop: 1900 * MM,
  ventY: 300 * MM,
  /** `PORT_*` — the inlet/outlet stubs. */
  portD: 110 * MM,
  inletZ: 1100 * MM,
  outletZ: 420 * MM,
  /** The vessel wall's inset for the water bodies (`PL_RY/RZ - 8 mm`). */
  waterInset: 8 * MM,
} as const;

/** The three chambers' fill fractions from the plant's one reading. */
export function wasteChamberLevels(levelPct: unknown): [number, number, number] {
  const base = waterTankLevelPct(levelPct) / 100;
  // An EMPTY plant is empty in every chamber: the aeration offset must not put
  // water in a plant the hub says is at 0 % (it did, until the guard).
  if (base <= 0) return [0, 0, 0];
  // …and a FULL plant is full in every chamber (the clarifier's lower offset is
  // a working level, not a deficit the tank keeps at overflow).
  if (base >= 1) return [1, 1, 1];
  return WASTE_PLANT.chamberRise.map((rise) => Math.min(1, Math.max(0, base + rise))) as [
    number,
    number,
    number,
  ];
}

/** One chamber's horizontal extent, between the baffles and the head. */
function chamberSpans(): Array<[number, number]> {
  const P = WASTE_PLANT;
  const half = P.length / 2;
  return [
    [-half + 0.006, P.baffleX[0] - P.baffleT / 2],
    [P.baffleX[0] + P.baffleT / 2, P.baffleX[1] - P.baffleT / 2],
    [P.baffleX[1] + P.baffleT / 2, half - 0.006],
  ];
}

/**
 * The plant's parts at a level, in the module's frame. Keys are stable per mesh
 * slot (`water-0..2`, `surface-0..2`, `lid-0..2`, …), so a re-apply as the
 * level moves can find the same meshes again.
 */
export function wasteTankParts(levelPct: unknown): WaterTankPart[] {
  const P = WASTE_PLANT;
  const out: WaterTankPart[] = [];

  // skids
  [-P.skidX, P.skidX].forEach((sx, index) => {
    out.push(tankBox(`skid-${index}`, 'pallet', sx - P.skidW / 2, -P.skidD / 2, 0, P.skidW, P.skidD, P.skidH));
  });

  // the vessel
  out.push(tankCylinderX('body', 'shell', -P.length / 2, P.zc, P.length, P.ry * 2, P.rz * 2));

  // two partition baffles: a full cross-section disc, so the chambers read
  // The baffles take the DARK trim tone, not the shell's: three pale discs in a
  // row wash the water out completely when seen through them (measured on the
  // demo canvas — the chambers read, the water did not).
  P.baffleX.forEach((bx, index) => {
    out.push(
      tankCylinderX(`baffle-${index}`, 'trim', bx - P.baffleT / 2, P.zc, P.baffleT,
        (P.ry - P.waterInset) * 2, (P.rz - P.waterInset) * 2),
    );
  });

  // green covers on the crown: a short neck, then the lid
  const crown = P.zc + P.rz;
  P.lidX.forEach((lx, index) => {
    out.push(tankCylinderZ(`lid-neck-${index}`, 'trim', lx, 0, crown - 0.004, P.lidNeckD, P.lidNeckD, P.lidNeckH));
    out.push(
      tankCylinderZ(`lid-${index}`, 'lid', lx, 0, crown - 0.004 + P.lidNeckH, P.lidD, P.lidD, P.lidH),
    );
  });

  // blower cabinet beside the +X head, with its riser and its run into the crown
  out.push(
    tankBox('blower', 'steel', P.blowerX - P.blowerW / 2, -P.blowerD / 2, 0, P.blowerW, P.blowerD, P.blowerH),
  );
  out.push(
    tankCylinderZ('air-riser', 'steel', P.blowerX, 0, P.blowerH, P.airPipeD, P.airPipeD, P.airPipeZ - P.blowerH),
  );
  out.push(
    tankCylinderX('air-run', 'steel', P.length / 2 - 0.04, P.airPipeZ, P.blowerX - (P.length / 2 - 0.04),
      P.airPipeD, P.airPipeD),
  );

  // vent stack
  const ventZ0 = crown - 0.03;
  out.push(tankCylinderZ('vent', 'trim', 0, P.ventY, ventZ0, P.ventD, P.ventD, P.ventTop - ventZ0));

  // inlet (raw, high on −X) and outlet (treated, low on +X)
  out.push(tankCylinderX('inlet', 'trim', -P.length / 2 - 0.12, P.inletZ, 0.12, P.portD, P.portD));
  out.push(tankCylinderX('outlet', 'trim', P.length / 2, P.outletZ, 0.11, P.portD, P.portD));

  // one water body + surface per chamber, each at its own level
  const levels = wasteChamberLevels(levelPct);
  const zBot = P.zc - P.rz;
  chamberSpans().forEach(([x0, x1], index) => {
    const fill = levels[index];
    if (fill <= 0.005 || x1 - x0 <= 0) return;
    const zTop = zBot + fill * 2 * P.rz;
    const zLocal = zTop - P.zc;
    const profile = tankSegmentProfile(P.ry - P.waterInset, P.rz - P.waterInset, zLocal, WATER_TANK_SEGMENTS);
    out.push({
      key: `water-${index}`,
      material: 'water',
      shape: 'prism' as WaterTankPartShape,
      axis: 'x',
      center: [(x0 + x1) / 2, 0, P.zc],
      size: [x1 - x0, (P.ry - P.waterInset) * 2, (P.rz - P.waterInset) * 2],
      profile,
    });
    const t = Math.max(-1, Math.min(1, zLocal / (P.rz - P.waterInset)));
    const halfChord = (P.ry - P.waterInset) * Math.sqrt(Math.max(0, 1 - t * t));
    out.push(
      tankBox(`surface-${index}`, 'surface', x0, -halfChord, zTop - 0.004, x1 - x0, 2 * halfChord, 0.004),
    );
  });

  return out;
}
